// Leaving a project the user can no longer use.
//
// The backend checks on every request that the project a token names still
// exists and that the caller is still in it. When it is not, the call is
// refused with 403 and the code below. The app then drops the project token
// and shows the project list, whichever call noticed first: a page refresh,
// a background poll or a click.

import { clearAccess } from './access';

export const PROJECT_ACCESS_REVOKED = 'PROJECT_ACCESS_REVOKED';
export const REVOKED_NOTICE_KEY = 'projectAccessRevoked';
export const REVOKED_NOTICE =
  'You no longer have access to that project. Open another one to continue.';

let leaving = false;

export const isRevoked = (status, body) =>
  status === 403 && body?.error?.code === PROJECT_ACCESS_REVOKED;

export const leaveRevokedProject = (location = window.location) => {
  // Many calls are in flight at once; leave on the first refusal only.
  if (leaving) return;
  leaving = true;
  try {
    localStorage.removeItem('projectToken');
    sessionStorage.setItem(REVOKED_NOTICE_KEY, '1');
  } catch {
    // storage unavailable: the redirect still happens
  }
  clearAccess();
  location.assign('/projects');
};

// True once, right after the app left a project for this reason.
export const takeRevokedNotice = () => {
  try {
    if (!sessionStorage.getItem(REVOKED_NOTICE_KEY)) return false;
    sessionStorage.removeItem(REVOKED_NOTICE_KEY);
    return true;
  } catch {
    return false;
  }
};

export const installProjectAccessGuard = (axios, target = window) => {
  if (target.fetch && !target.fetch.projectAccessGuard) {
    const original = target.fetch.bind(target);
    const guarded = async (...args) => {
      const response = await original(...args);
      if (response.status === 403) {
        try {
          const body = await response.clone().json();
          if (isRevoked(response.status, body)) leaveRevokedProject();
        } catch {
          // not JSON: some other 403
        }
      }
      return response;
    };
    guarded.projectAccessGuard = true;
    target.fetch = guarded;
  }
  axios?.interceptors.response.use(undefined, (error) => {
    const response = error?.response;
    if (isRevoked(response?.status, response?.data)) leaveRevokedProject();
    return Promise.reject(error);
  });
};

// For tests.
export const resetProjectAccessGuard = () => {
  leaving = false;
};
